import { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Trash2, UserPlus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCreateTrainingExample, TrainingExtractedData } from "@/hooks/useParserTraining";
import { UseFormReturn } from "react-hook-form";
import { ScrollArea } from "@/components/ui/scroll-area";

const passengerSchema = z.object({
  name: z.string().min(1, "Name is required"),
  seat_number: z.string().optional(),
  ticket_number: z.string().optional(),
});

const flightSchema = z.object({
  airline: z.string().optional(),
  flight_number: z.string().optional(),
  departure_airport: z.string().optional(),
  arrival_airport: z.string().optional(),
  departure_time: z.string().optional(),
  arrival_time: z.string().optional(),
  confirmation_number: z.string().optional(),
  cabin_class: z.string().optional(),
  passengers: z.array(passengerSchema).optional(),
});

const hotelSchema = z.object({
  hotel_name: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  check_in_date: z.string().optional(),
  check_out_date: z.string().optional(),
  confirmation_number: z.string().optional(),
  room_type: z.string().optional(),
  guests: z.array(z.object({ name: z.string() })).optional(),
});

const carSchema = z.object({
  rental_company: z.string().optional(),
  pickup_location: z.string().optional(),
  dropoff_location: z.string().optional(),
  pickup_time: z.string().optional(),
  dropoff_time: z.string().optional(),
  confirmation_number: z.string().optional(),
  car_type: z.string().optional(),
});

const activitySchema = z.object({
  name: z.string().optional(),
  date: z.string().optional(),
  time: z.string().optional(),
  location: z.string().optional(),
  confirmation_number: z.string().optional(),
});

const formSchema = z.object({
  email_subject: z.string().optional(),
  email_content: z.string().min(50, "Email content must be at least 50 characters"),
  booking_type: z.enum(["flight", "hotel", "car", "activity"]),
  display_name: z.string().optional(),
  destination: z.string().optional(),
  flights: z.array(flightSchema).optional(),
  hotels: z.array(hotelSchema).optional(),
  cars: z.array(carSchema).optional(),
  activities: z.array(activitySchema).optional(),
});

type FormData = z.infer<typeof formSchema>;

interface FormFieldComponentProps {
  form: UseFormReturn<FormData>;
  index: number;
  onRemove: () => void;
  canRemove: boolean;
}

interface AddTrainingExampleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddTrainingExampleDialog({
  open,
  onOpenChange,
}: AddTrainingExampleDialogProps) {
  const createExample = useCreateTrainingExample();
  const [activeTab, setActiveTab] = useState("email");

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email_subject: "",
      email_content: "",
      booking_type: "flight",
      display_name: "",
      destination: "",
      flights: [{ passengers: [] }],
      hotels: [{ guests: [] }],
      cars: [{}],
      activities: [{}],
    },
  });

  const bookingType = form.watch("booking_type");

  const flightFields = useFieldArray({ control: form.control, name: "flights" });
  const hotelFields = useFieldArray({ control: form.control, name: "hotels" });
  const carFields = useFieldArray({ control: form.control, name: "cars" });
  const activityFields = useFieldArray({ control: form.control, name: "activities" });

  const onSubmit = (data: FormData) => {
    const extracted_data: TrainingExtractedData = {
      display_name: data.display_name,
      destination: data.destination,
    };

    if (data.booking_type === "flight" && data.flights) {
      extracted_data.flights = data.flights.filter(f => f.airline || f.flight_number);
    }
    if (data.booking_type === "hotel" && data.hotels) {
      extracted_data.hotels = data.hotels.filter(h => h.hotel_name);
    }
    if (data.booking_type === "car" && data.cars) {
      extracted_data.cars = data.cars.filter(c => c.rental_company);
    }
    if (data.booking_type === "activity" && data.activities) {
      extracted_data.activities = data.activities.filter(a => a.name);
    }

    createExample.mutate(
      {
        email_subject: data.email_subject || null,
        email_content: data.email_content,
        booking_type: data.booking_type,
        extracted_data,
      },
      {
        onSuccess: () => {
          form.reset();
          onOpenChange(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Add Parser Training Example</DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="email">Email Content</TabsTrigger>
                <TabsTrigger value="extracted">Extracted Data</TabsTrigger>
              </TabsList>

              <ScrollArea className="h-[60vh] mt-4 pr-4">
                <TabsContent value="email" className="space-y-4 mt-0">
                  <FormField
                    control={form.control}
                    name="booking_type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Booking Type</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="flight">Flight</SelectItem>
                            <SelectItem value="hotel">Hotel</SelectItem>
                            <SelectItem value="car">Car Rental</SelectItem>
                            <SelectItem value="activity">Activity</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email_subject"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Subject</FormLabel>
                        <FormControl>
                          <Input placeholder="Your Flight Confirmation #12345" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email_content"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Content</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Paste the full email content here..."
                            className="min-h-[300px] font-mono text-sm"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </TabsContent>

                <TabsContent value="extracted" className="space-y-4 mt-0">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="display_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Trip Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Hong Kong Business Trip" {...field} />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="destination"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Destination</FormLabel>
                          <FormControl>
                            <Input placeholder="Hong Kong" {...field} />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Flight Fields */}
                  {bookingType === "flight" && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-medium">Flights</h3>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => flightFields.append({ passengers: [] })}
                        >
                          <Plus className="h-4 w-4 mr-1" /> Add Flight
                        </Button>
                      </div>
                      {flightFields.fields.map((field, index) => (
                        <FlightFormFields
                          key={field.id}
                          form={form}
                          index={index}
                          onRemove={() => flightFields.remove(index)}
                          canRemove={flightFields.fields.length > 1}
                        />
                      ))}
                    </div>
                  )}

                  {/* Hotel Fields */}
                  {bookingType === "hotel" && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-medium">Hotels</h3>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => hotelFields.append({ guests: [] })}
                        >
                          <Plus className="h-4 w-4 mr-1" /> Add Hotel
                        </Button>
                      </div>
                      {hotelFields.fields.map((field, index) => (
                        <HotelFormFields
                          key={field.id}
                          form={form}
                          index={index}
                          onRemove={() => hotelFields.remove(index)}
                          canRemove={hotelFields.fields.length > 1}
                        />
                      ))}
                    </div>
                  )}

                  {/* Car Fields */}
                  {bookingType === "car" && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-medium">Car Rentals</h3>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => carFields.append({})}
                        >
                          <Plus className="h-4 w-4 mr-1" /> Add Car
                        </Button>
                      </div>
                      {carFields.fields.map((field, index) => (
                        <CarFormFields
                          key={field.id}
                          form={form}
                          index={index}
                          onRemove={() => carFields.remove(index)}
                          canRemove={carFields.fields.length > 1}
                        />
                      ))}
                    </div>
                  )}

                  {/* Activity Fields */}
                  {bookingType === "activity" && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-medium">Activities</h3>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => activityFields.append({})}
                        >
                          <Plus className="h-4 w-4 mr-1" /> Add Activity
                        </Button>
                      </div>
                      {activityFields.fields.map((field, index) => (
                        <ActivityFormFields
                          key={field.id}
                          form={form}
                          index={index}
                          onRemove={() => activityFields.remove(index)}
                          canRemove={activityFields.fields.length > 1}
                        />
                      ))}
                    </div>
                  )}
                </TabsContent>
              </ScrollArea>
            </Tabs>

            <div className="flex justify-end gap-2 pt-4 border-t mt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createExample.isPending}>
                {createExample.isPending ? "Saving..." : "Save Example"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// Flight Form Fields Component
function FlightFormFields({ form, index, onRemove, canRemove }: FormFieldComponentProps) {
  const passengerFields = useFieldArray({
    control: form.control,
    name: `flights.${index}.passengers`,
  });

  return (
    <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Flight {index + 1}</span>
        {canRemove && (
          <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FormField
          control={form.control}
          name={`flights.${index}.airline`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs">Airline</FormLabel>
              <FormControl>
                <Input placeholder="Cathay Pacific" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`flights.${index}.flight_number`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs">Flight Number</FormLabel>
              <FormControl>
                <Input placeholder="CX846" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`flights.${index}.departure_airport`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs">From</FormLabel>
              <FormControl>
                <Input placeholder="SFO" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`flights.${index}.arrival_airport`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs">To</FormLabel>
              <FormControl>
                <Input placeholder="HKG" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`flights.${index}.departure_time`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs">Departure</FormLabel>
              <FormControl>
                <Input type="datetime-local" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`flights.${index}.arrival_time`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs">Arrival</FormLabel>
              <FormControl>
                <Input type="datetime-local" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`flights.${index}.confirmation_number`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs">Confirmation #</FormLabel>
              <FormControl>
                <Input placeholder="ABC123" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`flights.${index}.cabin_class`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs">Cabin Class</FormLabel>
              <FormControl>
                <Input placeholder="Business" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      {/* Passengers */}
      <div className="pt-2 border-t">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-muted-foreground">Passengers</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => passengerFields.append({ name: "" })}
          >
            <UserPlus className="h-3 w-3 mr-1" /> Add
          </Button>
        </div>
        {passengerFields.fields.map((pField, pIndex) => (
          <div key={pField.id} className="grid grid-cols-4 gap-2 mb-2">
            <FormField
              control={form.control}
              name={`flights.${index}.passengers.${pIndex}.name`}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input placeholder="Name" className="text-sm" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`flights.${index}.passengers.${pIndex}.seat_number`}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input placeholder="Seat" className="text-sm" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`flights.${index}.passengers.${pIndex}.ticket_number`}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input placeholder="Ticket #" className="text-sm" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => passengerFields.remove(pIndex)}
            >
              <Trash2 className="h-3 w-3 text-destructive" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Hotel Form Fields Component
function HotelFormFields({ form, index, onRemove, canRemove }: FormFieldComponentProps) {
  const guestFields = useFieldArray({
    control: form.control,
    name: `hotels.${index}.guests`,
  });

  return (
    <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Hotel {index + 1}</span>
        {canRemove && (
          <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FormField
          control={form.control}
          name={`hotels.${index}.hotel_name`}
          render={({ field }) => (
            <FormItem className="col-span-2">
              <FormLabel className="text-xs">Hotel Name</FormLabel>
              <FormControl>
                <Input placeholder="JW Marriott Hotel Hong Kong" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`hotels.${index}.address`}
          render={({ field }) => (
            <FormItem className="col-span-2">
              <FormLabel className="text-xs">Address</FormLabel>
              <FormControl>
                <Input placeholder="Pacific Place, 88 Queensway" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`hotels.${index}.city`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs">City</FormLabel>
              <FormControl>
                <Input placeholder="Hong Kong" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`hotels.${index}.room_type`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs">Room Type</FormLabel>
              <FormControl>
                <Input placeholder="Deluxe Room" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`hotels.${index}.check_in_date`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs">Check-in</FormLabel>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`hotels.${index}.check_out_date`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs">Check-out</FormLabel>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`hotels.${index}.confirmation_number`}
          render={({ field }) => (
            <FormItem className="col-span-2">
              <FormLabel className="text-xs">Confirmation #</FormLabel>
              <FormControl>
                <Input placeholder="78441621" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      {/* Guests */}
      <div className="pt-2 border-t">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-muted-foreground">Guests</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => guestFields.append({ name: "" })}
          >
            <UserPlus className="h-3 w-3 mr-1" /> Add
          </Button>
        </div>
        {guestFields.fields.map((gField, gIndex) => (
          <div key={gField.id} className="flex gap-2 mb-2">
            <FormField
              control={form.control}
              name={`hotels.${index}.guests.${gIndex}.name`}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <Input placeholder="Guest name" className="text-sm" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => guestFields.remove(gIndex)}
            >
              <Trash2 className="h-3 w-3 text-destructive" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Car Form Fields Component
function CarFormFields({ form, index, onRemove, canRemove }: FormFieldComponentProps) {
  return (
    <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Car Rental {index + 1}</span>
        {canRemove && (
          <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FormField
          control={form.control}
          name={`cars.${index}.rental_company`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs">Company</FormLabel>
              <FormControl>
                <Input placeholder="Hertz" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`cars.${index}.car_type`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs">Car Type</FormLabel>
              <FormControl>
                <Input placeholder="Midsize SUV" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`cars.${index}.pickup_location`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs">Pickup Location</FormLabel>
              <FormControl>
                <Input placeholder="SFO Airport" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`cars.${index}.dropoff_location`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs">Dropoff Location</FormLabel>
              <FormControl>
                <Input placeholder="LAX Airport" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`cars.${index}.pickup_time`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs">Pickup Time</FormLabel>
              <FormControl>
                <Input type="datetime-local" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`cars.${index}.dropoff_time`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs">Dropoff Time</FormLabel>
              <FormControl>
                <Input type="datetime-local" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`cars.${index}.confirmation_number`}
          render={({ field }) => (
            <FormItem className="col-span-2">
              <FormLabel className="text-xs">Confirmation #</FormLabel>
              <FormControl>
                <Input placeholder="H12345678" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}

// Activity Form Fields Component
function ActivityFormFields({ form, index, onRemove, canRemove }: FormFieldComponentProps) {
  return (
    <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Activity {index + 1}</span>
        {canRemove && (
          <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FormField
          control={form.control}
          name={`activities.${index}.name`}
          render={({ field }) => (
            <FormItem className="col-span-2">
              <FormLabel className="text-xs">Activity Name</FormLabel>
              <FormControl>
                <Input placeholder="City Tour" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`activities.${index}.location`}
          render={({ field }) => (
            <FormItem className="col-span-2">
              <FormLabel className="text-xs">Location</FormLabel>
              <FormControl>
                <Input placeholder="Central, Hong Kong" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`activities.${index}.date`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs">Date</FormLabel>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`activities.${index}.time`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs">Time</FormLabel>
              <FormControl>
                <Input type="time" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`activities.${index}.confirmation_number`}
          render={({ field }) => (
            <FormItem className="col-span-2">
              <FormLabel className="text-xs">Confirmation #</FormLabel>
              <FormControl>
                <Input placeholder="ACT12345" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
